import type { SearchHit } from '@proerp/shared';
import { useOpenItems } from '@/features/accounting/accountingApi';
import { useContacts, useDirectoryContact } from '@/features/crm/contactsApi';
import { useCustomer } from '@/features/crm/customersApi';
import { useArticle } from '@/features/inventory/articlesApi';
import { useOpenIncoming, usePurchaseOrder } from '@/features/procurement/procurementApi';
import { useProject } from '@/features/projects/projectsApi';
import { useSalesDocument } from '@/features/sales/salesApi';
import { usePermissions } from '@/lib/useCurrentUser';
import { useUserOptions } from '@/lib/useUserOptions';
import { projectLeadLabel } from '@/features/projects/projectLeads';
import {
  articleAnswer,
  contactAnswer,
  customerAnswer,
  fallbackAnswer,
  projectAnswer,
  purchaseOrderAnswer,
  salesDocumentAnswer,
  type HitAnswer,
} from './hitAnswer';

const INVOICE_KINDS = new Set(['INVOICE', 'ADVANCE_INVOICE', 'FINAL_INVOICE']);

/**
 * Antwort zum markierten Treffer laden (Vorschlag C). Alle Hooks laufen immer
 * (Hook-Regeln), aber nur der zum Treffertyp fragt an — die übrigen bekommen eine
 * leere ID und bleiben aus. Die OP-Liste nur mit `accounting.read` und nur für
 * Kunden und Rechnungen; sie ist im Cache meist schon da (Dashboard, Buchhaltung).
 *
 * Solange geladen wird, steht die Antwort aus dem Treffer allein (Öffnen,
 * Hintergrund-Tab, Kopieren) — die Tastatur funktioniert also sofort.
 */
export interface HitAnswerState {
  answer: HitAnswer | null;
  /** Details (oder ein Teil davon) werden noch geladen. */
  isLoading: boolean;
  /** Details ließen sich nicht laden — die Karte sagt das, statt still weniger zu zeigen. */
  isError: boolean;
}

export function useHitAnswer(hit: SearchHit | null): HitAnswerState {
  const { can } = usePermissions();
  const idFor = (type: SearchHit['type']) => (hit?.type === type ? hit.id : '');

  const article = useArticle(idFor('article'));
  const incoming = useOpenIncoming(idFor('article') ? [idFor('article')] : []);
  const customer = useCustomer(idFor('customer'));
  const contacts = useContacts(idFor('customer'));
  const contact = useDirectoryContact(idFor('contact'));
  const project = useProject(idFor('project'));
  const users = useUserOptions().data?.data;
  const salesDocument = useSalesDocument(idFor('sales-document'));
  const purchaseOrder = usePurchaseOrder(idFor('purchase-order'));
  const needsOpenItems =
    can('accounting.read') &&
    (hit?.type === 'customer' || (hit?.type === 'sales-document' && INVOICE_KINDS.has(hit.badge ?? '')));
  const openItems = useOpenItems({ enabled: needsOpenItems });

  if (!hit) return { answer: null, isLoading: false, isError: false };
  const openItemList = needsOpenItems ? (openItems.data?.data ?? null) : null;

  switch (hit.type) {
    case 'article': {
      const data = article.data?.data;
      if (!data) break;
      return {
        answer: articleAnswer(hit, data, incoming.data?.find((entry) => entry.articleId === data.id) ?? null, can),
        isLoading: incoming.isLoading,
        isError: incoming.isError,
      };
    }
    case 'customer': {
      const data = customer.data?.data;
      if (!data) break;
      const list = contacts.data?.data ?? [];
      const primary = list.find((entry) => entry.isPrimary) ?? list[0] ?? null;
      return {
        answer: customerAnswer(hit, data, primary, openItemList, can),
        isLoading: contacts.isLoading || (needsOpenItems && openItems.isLoading),
        isError: contacts.isError || (needsOpenItems && openItems.isError),
      };
    }
    case 'contact': {
      const data = contact.data?.data;
      if (!data) break;
      return { answer: contactAnswer(hit, data), isLoading: false, isError: false };
    }
    case 'project': {
      const data = project.data?.data;
      if (!data) break;
      return { answer: projectAnswer(hit, data, can, projectLeadLabel(data, users)), isLoading: false, isError: false };
    }
    case 'sales-document': {
      const data = salesDocument.data?.data;
      if (!data) break;
      const openItem =
        openItemList?.find((item) => item.direction === 'RECEIVABLE' && item.invoiceId === data.id) ?? null;
      return {
        answer: salesDocumentAnswer(hit, data, openItem, can),
        isLoading: needsOpenItems && openItems.isLoading,
        isError: needsOpenItems && openItems.isError,
      };
    }
    case 'purchase-order': {
      const data = purchaseOrder.data?.data;
      if (!data) break;
      return { answer: purchaseOrderAnswer(hit, data), isLoading: false, isError: false };
    }
    default:
      return { answer: fallbackAnswer(hit), isLoading: false, isError: false };
  }

  const isLoading =
    article.isLoading ||
    customer.isLoading ||
    contact.isLoading ||
    project.isLoading ||
    salesDocument.isLoading ||
    purchaseOrder.isLoading;
  const isError =
    article.isError ||
    customer.isError ||
    contact.isError ||
    project.isError ||
    salesDocument.isError ||
    purchaseOrder.isError;
  return { answer: fallbackAnswer(hit), isLoading, isError };
}
